import {
  Box,
  Button,
  type ButtonOwnProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";
import React, {
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

import modalEmitter from "./modalEmitter";
import {
  actionsStyles,
  bodyStyles,
  btnStyle,
  buttonWrapStyles,
  containerStyles,
  paperCenterStyles,
  paperRightHalfStyles,
  paperStyles,
  paperStylesFullWH,
  paperStylesTallCentered,
  titleStyles,
} from "./style";
import { IconClose } from "../../icons/IconClose";

interface IActionsButton {
  text?: string;
  callBack?: () => void;
  asyncCallBack?: () => Promise<boolean | void>;
  variant?: ButtonOwnProps["variant"];
  visible?: boolean;
  isLoading?: boolean;
}

interface IDialogProps {
  body: string | ReactNode;
  fullWidthHeight?: boolean;
  modalToCentrPosition?: boolean;
  modalRightHalf?: boolean;
  /** По центру окна по вертикали; высота минимум ~80vh, максимум почти fullscreen; скролл в теле */
  tallCentered?: boolean;
  title?: string;
  afterCloseCallBack?: () => void;
}

export interface IModalContent {
  dialog: IDialogProps;
  closeAtion?: IActionsButton;
  confirmAtion?: IActionsButton;
  customAtion?: IActionsButton;
}

const ModalComponent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<IModalContent | null>(null);
  const afterCloseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const openHandler = (modalContent: IModalContent) => {
      afterCloseRef.current = modalContent.dialog.afterCloseCallBack || null;
      setContent(modalContent);
      setIsOpen(true);
    };

    const closeHandler = (callback?: () => void) => {
      const afterClose = afterCloseRef.current;
      setIsOpen(false);
      setContent(null);
      afterCloseRef.current = null;
      if (afterClose) afterClose();
      if (callback) callback();
    };

    modalEmitter.on("openModal", openHandler);
    modalEmitter.on("closeModal", closeHandler);

    return () => {
      modalEmitter.off("openModal", openHandler);
      modalEmitter.off("closeModal", closeHandler);
    };
  }, []);

  const close = () => {
    const afterClose = afterCloseRef.current;
    setIsOpen(false);
    setContent(null);
    afterCloseRef.current = null;
    if (afterClose) afterClose();
  };

  const handleAction = async (action?: IActionsButton) => {
    if (!action) return;
    if (action.asyncCallBack) {
      const result = await action.asyncCallBack();
      if (result) {
        close();
        return;
      }
    }
    if (action.callBack) {
      action.callBack();
      close();
    }
  };

  const paperSx = useMemo(() => {
    if (content?.dialog?.modalRightHalf)
      return paperRightHalfStyles as SxProps<Theme>;
    if (content?.dialog?.modalToCentrPosition)
      return paperCenterStyles as SxProps<Theme>;
    if (content?.dialog?.tallCentered)
      return paperStylesTallCentered as SxProps<Theme>;
    if (content?.dialog?.fullWidthHeight)
      return paperStylesFullWH as SxProps<Theme>;
    return paperStyles;
  }, [content]);

  const dialogContainerSx = useMemo(
    () =>
      content?.dialog?.tallCentered
        ? { alignItems: "center", justifyContent: "center" }
        : containerStyles,
    [content?.dialog?.tallCentered],
  );

  if (!content) return null;

  const { dialog, closeAtion, confirmAtion, customAtion } = content;
  const tallCentered = Boolean(dialog.tallCentered);

  const actions = [
    {
      ...closeAtion,
      defaultText: "Закрыть",
      variant: closeAtion?.variant || "contained",
      onClick: () => close(),
    },
    {
      ...confirmAtion,
      defaultText: "Подтвердить",
      onClick: () => handleAction(confirmAtion),
    },
    {
      ...customAtion,
      defaultText: "Действие",
      onClick: () => handleAction(customAtion),
    },
  ].filter((action) => action?.visible);

  const actionVisible =
    Boolean(closeAtion?.visible) ||
    Boolean(confirmAtion?.visible) ||
    Boolean(customAtion?.visible);

  return ReactDOM.createPortal(
    <Dialog
      id="custom-modal"
      sx={{ "& .MuiDialog-container": dialogContainerSx }}
      open={isOpen}
      onClose={close}
      fullWidth
      slotProps={{
        paper: {
          sx: paperSx,
        },
      }}
    >
      <Stack
        sx={{ ...titleStyles, ...(tallCentered ? { flexShrink: 0 } : {}) }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap="8px"
      >
        <DialogTitle
          sx={{ padding: 0, margin: 0, maxWidth: "calc(100% - 50px)" }}
          variant="h6"
        >
          {dialog.title || "Ошибка"}
        </DialogTitle>
        <Box>
          <IconButton onClick={close}>
            <IconClose fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
      <DialogContent
        sx={{
          ...bodyStyles,
          ...(tallCentered
            ? {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                maxHeight: "none",
                overflow: "hidden",
              }
            : { maxHeight: actionVisible ? "77vh" : "85vh" }),
        }}
      >
        {dialog.body}
      </DialogContent>
      {actionVisible && (
        <DialogActions sx={actionsStyles}>
          <Stack sx={buttonWrapStyles}>
            {actions.map(
              ({ text, defaultText, variant, onClick, isLoading }, index) => {
                return (
                  <Button
                    key={index}
                    sx={btnStyle}
                    variant={variant || "outlined"}
                    onClick={onClick}
                    startIcon={
                      isLoading && (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      )
                    }
                  >
                    {text || defaultText}
                  </Button>
                );
              },
            )}
          </Stack>
        </DialogActions>
      )}
    </Dialog>,
    document.getElementById("modal-root")!,
  );
};

export default ModalComponent;
